import { useMutation } from '@apollo/client/react';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { TeamSpot } from '@optools/components/team-spot';
import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import { Card, CardContent } from '@optools/components/ui/card';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@optools/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { cn } from '@optools/components/ui/utils';
import { UPDATE_PARTNER_DISTRIBUTION_LOCK } from '@optools/services/operator-gateway/btc/mutations';
import { GET_ZIXI_SOURCES_BY_TEAM } from '@optools/services/operator-gateway/btc/queries';
import { PipelineConfigInfo } from '@optools/services/operator-gateway/pipeline-config/types';
import { SkywalkerZixiSource } from '@optools/services/operator-gateway/types/generated/graphql';
import { capitalize } from 'es-toolkit';
import { LockIcon, TriangleAlert, UnlockIcon } from 'lucide-react';
import { toast } from 'sonner';

export interface ZixiSourcesCardProps {
  partnerLockEnabled?: PipelineConfigInfo['partnerLockEnabled'];
  partnerLockExclusions?: PipelineConfigInfo['partnerLockExclusions'];
  sources: SkywalkerZixiSource[] | undefined;
  teamId?: PipelineConfigInfo['teamId'];
}

export function ZixiSourcesCard({
  partnerLockEnabled,
  partnerLockExclusions,
  sources,
  teamId,
}: ZixiSourcesCardProps) {
  const [updatePartnerLock, { loading }] = useMutation(UPDATE_PARTNER_DISTRIBUTION_LOCK, {
    awaitRefetchQueries: true,
    onCompleted: () => {
      toast.success('Updated Partner Lock', {
        description: `Partner lock for ${teamId} Zixi sources has been updated`,
      });
    },
    refetchQueries: [{ query: GET_ZIXI_SOURCES_BY_TEAM }],
  });

  const allSourcesExcluded = sources?.every(({ zixiSourceName }) =>
    partnerLockExclusions?.includes(zixiSourceName),
  );

  const noZixiSources = !sources || sources.length === 0;

  const handlePartnerLockChange = (setLock: boolean) => {
    if (!teamId) {
      return;
    }

    updatePartnerLock({ variables: { setLock, teamId } });
  };

  const toolTipMessage = () => {
    if (!partnerLockEnabled) {
      return 'Partner lock orchestration is not enabled, Zixi sources cannot be locked or unlocked';
    } else if (noZixiSources) {
      return 'No Zixi sources for this team, sources cannot be locked or unlocked';
    } else {
      return 'All Zixi sources are excluded from being locked or unlocked';
    }
  };

  return (
    <Card data-testid={`${teamId}-zixi-source-card`}>
      <CardContent className="flex h-96 w-xl flex-col items-start justify-start gap-2 p-4">
        <Tooltip>
          <TooltipTrigger
            className={cn(partnerLockEnabled && !allSourcesExcluded && 'invisible', 'self-end')}
            data-testid="zixi-source-lock-warning">
            <TriangleAlert className="size-4 stroke-amber-400" />
          </TooltipTrigger>
          <TooltipContent className="text-xs">{toolTipMessage()}</TooltipContent>
        </Tooltip>
        <div className="flex w-full items-center justify-between gap-5">
          <div className="flex gap-5">
            <TeamSpot id={teamId} showAbbreviation={false} size={100} />
            <div className="flex flex-col gap-2">
              <div className="flex flex-col">
                <h4 className="text-sm font-semibold">Team ID</h4>
                <span className="text-sm">{teamId}</span>
              </div>
              <div className="flex flex-col">
                <h4 className="text-sm font-semibold">Partner Lock Orchestration</h4>
                <Badge
                  data-testid="partner-lock-badge"
                  variant={partnerLockEnabled ? 'default' : 'secondary'}>
                  {partnerLockEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <ConfirmationDialog
              description={`Are you sure you want to lock Zixi sources for ${teamId}?`}
              title="Lock Zixi Sources"
              onConfirm={() => handlePartnerLockChange(true)}>
              <Button
                disabled={!partnerLockEnabled || allSourcesExcluded || loading || noZixiSources}
                loading={loading}>
                <LockIcon /> Lock Zixi Sources
              </Button>
            </ConfirmationDialog>
            <ConfirmationDialog
              description={`Are you sure you want to unlock the Zixi sources for ${teamId}?`}
              title="Unlock Zixi Sources"
              onConfirm={() => handlePartnerLockChange(false)}>
              <Button
                disabled={!partnerLockEnabled || allSourcesExcluded || loading || noZixiSources}
                loading={loading}>
                <UnlockIcon /> Unlock Zixi Sources
              </Button>
            </ConfirmationDialog>
          </div>
        </div>
        {!noZixiSources ? (
          <Table className="w-auto table-fixed text-xs" containerClassName="w-fit">
            <TableCaption className="sr-only">All the Zixi sources for the team</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Source Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Active Source</TableHead>
                <TableHead>Partner Lock Exclusion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sources.map((source) => {
                const isExcluded = partnerLockExclusions?.some(
                  (item) => item === source.zixiSourceName,
                );
                return (
                  <TableRow key={teamId + source.zixiSourceName}>
                    <TableCell className="max-w-60 truncate">{source.zixiSourceName}</TableCell>
                    <TableCell className="max-w-24 truncate">{source.location}</TableCell>
                    <TableCell className="max-w-24 truncate">{source.destination}</TableCell>
                    <TableCell className="max-w-28 truncate">{source.activeSource}</TableCell>
                    <TableCell className="max-w-28 truncate">
                      <Badge variant={isExcluded ? 'default' : 'secondary'}>
                        {capitalize(String(isExcluded))}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <div className="flex w-full flex-1 items-center justify-center">
            <p className="self-center">No Zixi Sources found</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
